import {
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail
} from 'firebase/auth';
import { userService } from './user.js';
import { clientService } from './client.js';
import { agencyService } from './agency.js';

// Map privado para estado do header do cliente (substitui window.clientHeaderState)
const clientHeaderStateMap = new Map();

class AuthService {
  constructor() {
    this.auth = null;
    this.currentUser = null;
    this.listeners = [];
    this.isSigningIn = false;
    this.isInitialized = false;
    this._authUnsubscribe = null; // Para cleanup do listener Firebase
  }

  // Getter/setter para clientHeaderState (backward compatible)
  static getClientHeaderState() {
    return clientHeaderStateMap.get('state') || null;
  }

  static setClientHeaderState(state) {
    if (state === null) {
      clientHeaderStateMap.delete('state');
    } else {
      clientHeaderStateMap.set('state', state);
    }
  }

  static clearClientHeaderState() {
    clientHeaderStateMap.clear();
  }

  initialize(auth) {
    this.auth = auth;

    // Cleanup listener anterior se existir
    if (this._authUnsubscribe) {
      this._authUnsubscribe();
    }

    // Timeout fallback - se auth não inicializar em 8s, forçar inicialização
    // Isso previne que o app fique travado no preloader
    const authTimeout = setTimeout(() => {
      if (!this.isInitialized) {
        this.currentUser = null;
        this.isInitialized = true;
        this.notifyListeners(null);
      }
    }, 8000);

    this._authUnsubscribe = onAuthStateChanged(this.auth, async (firebaseUser) => {
      // Limpar timeout já que auth respondeu
      clearTimeout(authTimeout);
      try {
        if (firebaseUser) {
          // Initialize user data and load role-based information
          const user = await userService.initializeUser(firebaseUser);
          this.currentUser = user;

          // Limpar qualquer sessão de cliente quando agência faz login
          // Agência tem prioridade sobre cliente
          if (userService.isAgency()) {
            const clientAuth = sessionStorage.getItem('clientAuth');
            if (clientAuth) {
              sessionStorage.removeItem('clientAuth');
              // Usar Map privado em vez de window global
              AuthService.clearClientHeaderState();
            }

            await agencyService.initialize();
            // Try to initialize client service - it will skip if agency not ready
            await clientService.initialize();
          }

          this.isInitialized = true;
          this.notifyListeners(user);

          // Auto-redirect to dashboard if on login or landing page after successful login
          if (typeof window !== 'undefined' && window.location) {
            const currentPath = window.location.pathname;
            if (currentPath === '/login' || currentPath === '/') {
              setTimeout(() => {
                if (window.router) {
                  window.router.navigate('/dashboard');
                }
              }, 200);
            }
          }
        } else {
          // User signed out - cleanup services
          this.currentUser = null;
          userService.clearUserData();
          clientService.cleanup();
          agencyService.cleanup();
          this.isInitialized = true;
          this.notifyListeners(null);
        }
      } catch (error) {
        this.currentUser = null;
        this.isInitialized = true;
        this.notifyListeners(null);
      }
    });
  }

  /**
   * Wait for auth to be initialized
   * Returns a promise that resolves when auth state is determined
   */
  waitForAuth() {
    if (this.isInitialized) {
      return Promise.resolve(this.currentUser);
    }
    return new Promise((resolve) => {
      const unsubscribe = this.onAuthStateChanged((user) => {
        if (this.isInitialized) {
          unsubscribe();
          resolve(user);
        }
      });
    });
  }

  async signInWithGoogle() {
    if (this.isSigningIn) {
      throw new Error('Sign-in already in progress');
    }

    try {
      this.isSigningIn = true;
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      const result = await signInWithPopup(this.auth, provider);
      return result.user;
    } catch (error) {
      throw error;
    } finally {
      this.isSigningIn = false;
    }
  }

  async signInWithEmail(email, password) {
    if (this.isSigningIn) {
      throw new Error('Sign-in already in progress');
    }

    try {
      this.isSigningIn = true;
      const result = await signInWithEmailAndPassword(this.auth, email, password);
      return result.user;
    } catch (error) {
      throw error;
    } finally {
      this.isSigningIn = false;
    }
  }

  async createAccount(email, password) {
    if (this.isSigningIn) {
      throw new Error('Account creation already in progress');
    }

    try {
      this.isSigningIn = true;
      const result = await createUserWithEmailAndPassword(this.auth, email, password);
      return result.user;
    } catch (error) {
      throw error;
    } finally {
      this.isSigningIn = false;
    }
  }

  async signInOrCreateAccount(email, password) {
    try {
      // Primeiro tenta fazer login
      const userCredential = await this.signInWithEmail(email, password);
      return { user: userCredential };
    } catch (signInError) {
      if (signInError.code === 'auth/user-not-found' || 
          signInError.code === 'auth/invalid-credential') {
        // Se usuário não existe, cria a conta
        try {
          const userCredential = await this.createAccount(email, password);
          return { user: userCredential };
        } catch (createError) {
          if (createError.code === 'auth/email-already-in-use') {
            // Email existe mas senha está errada
            throw new Error('Email já cadastrado. Verifique sua senha.');
          } else {
            throw createError;
          }
        }
      } else {
        throw signInError;
      }
    }
  }

  async sendPasswordReset(email) {
    try {
      await sendPasswordResetEmail(this.auth, email);
      return true;
    } catch (error) {
      throw error;
    }
  }

  async signOut() {
    try {
      // 1. Limpar cache de tokens de API (CRÍTICO - evita vazamento)
      try {
        const { apiTokensService } = await import('./api-tokens.js');
        apiTokensService.clearCache();
      } catch (e) {
        // Ignorar se serviço não disponível
      }

      // 2. Limpar listeners internos
      this.listeners = [];

      // 3. Limpar OAuth states em sessionStorage
      this._clearOAuthStates();

      // 4. Limpar estado do header do cliente
      AuthService.clearClientHeaderState();

      // 5. Limpar sessionStorage de cliente
      sessionStorage.removeItem('clientAuth');
      sessionStorage.removeItem('agencySettings');

      // 5.5 Desativar modo demo
      try {
        const { demoModeService } = await import('./demo-mode.js');
        demoModeService.disable();
      } catch (e) { /* demo-mode cleanup optional */ }

      // 6. Firebase signOut (último para garantir cleanup antes)
      await signOut(this.auth);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Limpa todos os OAuth states do sessionStorage
   * Previne reutilização de tokens após logout
   */
  _clearOAuthStates() {
    const oauthKeys = [
      'google_ads_oauth_state',
      'meta_oauth_state',
      'oauth_state',
      'oauth_code_verifier'
    ];

    oauthKeys.forEach(key => {
      try {
        sessionStorage.removeItem(key);
      } catch (e) {
        // Ignorar erros de storage
      }
    });
  }

  onAuthStateChanged(callback) {
    this.listeners.push(callback);
    // Call immediately with current state
    if (this.currentUser !== undefined) {
      callback(this.currentUser);
    }
  }

  notifyListeners(user) {
    this.listeners.forEach(callback => callback(user));
  }

  getUserProfile() {
    if (!this.currentUser) return null;
    
    return {
      uid: this.currentUser.uid,
      email: this.currentUser.email,
      displayName: this.currentUser.displayName,
      photoURL: this.currentUser.photoURL,
      role: this.currentUser.role,
      agencyId: this.currentUser.agencyId,
      status: this.currentUser.status
    };
  }

  getCurrentUser() {
    return this.currentUser;
  }

  getCurrentAgency() {
    return userService.getCurrentAgency();
  }

  getCurrentClient() {
    return userService.getCurrentClient();
  }

  isAgency() {
    return userService.isAgency();
  }

  isClient() {
    return userService.isClient();
  }

  getUserRole() {
    return userService.getUserRole();
  }

  isAuthenticated() {
    return !!this.currentUser;
  }

  async getIdToken() {
    if (!this.auth?.currentUser) return null;
    return this.auth.currentUser.getIdToken();
  }

  canAccessAgencyData(agencyId) {
    return userService.canAccessAgencyData(agencyId);
  }

  canAccessClientData(agencyId, clientId) {
    return userService.canAccessClientData(agencyId, clientId);
  }

  canManageAgencySettings() {
    return userService.canManageAgencySettings();
  }

  getUserDisplayName() {
    return userService.getUserDisplayName();
  }

  getAgencyDisplayName() {
    return userService.getAgencyDisplayName();
  }

  hasFeature(featureName) {
    return userService.hasFeature(featureName);
  }

  isSubscriptionActive() {
    return userService.isSubscriptionActive();
  }

  isSignInInProgress() {
    return this.isSigningIn;
  }
}

export const authService = new AuthService();
export { AuthService };